import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { BATCH, enableBatching } from 'redux-batched-actions';
import createSagaMiddleware, { Action, stdChannel } from 'redux-saga';

import { Environment } from '../../common/types/environment';
import broadcastView from '../client/pages/broadcast-viewer/features/broadcast/slice';
import { getMyUserId, getRoomId, selectSessionId } from '../client/pages/call/selectors';
import callReducer from '../client/pages/call/slice';
import { conferencesHistoryReducer } from '../client/pages/conferences-history/slice';
import { userFeedbackReducer } from '../client/pages/feedback/features/user-feedback/slice';
import { configReducer } from '../client/shared/features/config';
import { getMediaPlatform, getRoomAgentConfig, getTelemostAgent } from '../client/shared/features/config/selectors';
import { deviceManagerReducer } from '../client/shared/features/device-manager/slice';
import { diagnosticsReducer } from '../client/shared/features/diagnostics/slice';
import { recordingReducer } from '../client/shared/features/download-record';
import feedbackReducer from '../client/shared/features/feedback/slice';
import { flagsReducer } from '../client/shared/features/flags';
import { selectIsWarmupModeEnabled } from '../client/shared/features/flags/selectors';
import { notificationsReducer } from '../client/shared/features/notifications';
import { parentFrameCommunicatorReducer } from '../client/shared/features/parent-frame-communicator/slice';
import { permissionsExtendedReducer } from '../client/shared/features/permissions-extended';
import { promozavrReducer } from '../client/shared/features/promozavr';
import { dtmfReducer } from '../client/shared/features/room-agent/dtmf/slice';
import { settingsReducer } from '../client/shared/features/settings';
import { settingsModalReducer } from '../client/shared/features/settings-modal/slice';
import { uatraitsReducer } from '../client/shared/features/uatraits';
import { userReducer } from '../client/shared/features/user';
import { selectUserTypes } from '../client/shared/features/user/selectors';
import { toLoggableError } from '../client/shared/helpers/errors';
import { addAdditionalProviders } from '../client/shared/helpers/loggerConfig';
import { getShortUrlId } from '../client/shared/helpers/navigation';
import { SessionState } from '../client/shared/helpers/session-state';
import { createRootSaga, logger as rootSagaLogger } from './sagas';

export const rootReducer = combineReducers({
    flags: flagsReducer,
    config: configReducer,
    ua: uatraitsReducer,
    user: userReducer,
    settings: settingsReducer,
    settingsModal: settingsModalReducer,
    permissionsExtended: permissionsExtendedReducer,
    diagnostics: diagnosticsReducer,
    call: callReducer,
    broadcastView: broadcastView,
    userFeedback: userFeedbackReducer,
    recording: recordingReducer,
    feedback: feedbackReducer,
    notifications: notificationsReducer,
    deviceManager: deviceManagerReducer,
    sessionId: String,
    skipOpenApp: Boolean,
    dtmf: dtmfReducer,
    parentFrameCommunicator: parentFrameCommunicatorReducer,
    conferencesHistory: conferencesHistoryReducer,
    promozavr: promozavrReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

const batchedReducer = enableBatching(rootReducer) as typeof rootReducer;

declare module 'react-redux' {
    interface DefaultRootState extends RootState {}
}

export const createStore = (preloadedState: Partial<RootState>) => {
    const channel = stdChannel<Action<string>>();
    const rawPut = channel.put;
    channel.put = (action: AnyAction) => {
        if (action.type === BATCH) {
            action.payload.forEach(rawPut);
            return;
        }
        rawPut(action);
    };
    const sagaMiddleware = createSagaMiddleware({
        onError: (error, errorInfo) => {
            rootSagaLogger.error(toLoggableError(error), {
                method: 'saga-middleware-error-handler',
                additional: {
                    kind: 'root_saga_error',
                    sagaStack: errorInfo.sagaStack,
                },
            });
        },
        channel,
    });

    const store = configureStore({
        reducer: batchedReducer,
        preloadedState,
        devTools: preloadedState.config?.environment !== Environment.PRODUCTION,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware({ thunk: false }).concat(sagaMiddleware),
    });

    const isWarmupModeEnabled = selectIsWarmupModeEnabled(preloadedState);
    if (!NODE && !isWarmupModeEnabled) {
        sagaMiddleware.run(createRootSaga());
    }
    return store;
};

export type RootStore = ReturnType<typeof createStore>;
let store: RootStore | undefined;

addAdditionalProviders(() => {
    const state = store?.getState();

    if (!state) {
        return {};
    }

    const roomAgent = getRoomAgentConfig(state);

    return {
        peer_id: getMyUserId(state),
        organizationIds: state.user?.organizationIds,
        ...(roomAgent
            ? {
                  room_agent_call_id: roomAgent.callId,
              }
            : undefined),
        media_platform: getMediaPlatform(state),
        room_id: getRoomId(state),
        client_instance_id: SessionState.instanceId,
        short_url_id: getShortUrlId(),
        telemost_agent: getTelemostAgent(state),
        user_types: selectUserTypes(state),
        session_id: selectSessionId(state),
        goloom_sdk_version: GOLOOM_SDK_VERSION,
        goloom_sdk_react_version: GOLOOM_SDK_REACT_VERSION,
        telemost_sdk_version: TELEMOST_SDK_VERSION,
    };
});

export const initStore = (preloadedState: Partial<RootState>): RootStore => {
    store = createStore(preloadedState);
    return store;
};

export const getStore = (): RootStore => {
    if (!store) {
        store = createStore({});
    }

    return store;
};
